import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { PickingSortMode, DEFAULT_PICKING_SORT_MODE } from 'src/entities/pickingListEntity';

export type Props = {
  open: boolean;
  shipperNames: string[];
  isLoading: boolean;
  isError: boolean;
  barcodeSelectable?: boolean;
  sortModeSelectable?: boolean;
  onBack: () => void;
  onConfirm: (selected: string[], barcodeSelected?: string[], sortMode?: PickingSortMode) => void;
};

const ShipperSelectDialog: FC<Props> = memo(({
  open,
  shipperNames,
  isLoading,
  isError,
  barcodeSelectable = false,
  sortModeSelectable = false,
  onBack,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [barcodeSelected, setBarcodeSelected] = useState<Set<string>>(new Set());
  const [sortMode, setSortMode] = useState<PickingSortMode>(DEFAULT_PICKING_SORT_MODE);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setBarcodeSelected(new Set());
      setSortMode(DEFAULT_PICKING_SORT_MODE);
    }
  }, [open]);

  const allSelected = useMemo(
    () => shipperNames.length > 0 && shipperNames.every((name) => selected.has(name)),
    [shipperNames, selected],
  );
  const someSelected = selected.size > 0 && !allSelected;

  const selectedArray = useMemo(() => Array.from(selected), [selected]);
  const allBarcodeSelected = useMemo(
    () => selectedArray.length > 0 && selectedArray.every((name) => barcodeSelected.has(name)),
    [selectedArray, barcodeSelected],
  );
  const someBarcodeSelected = useMemo(
    () => selectedArray.some((name) => barcodeSelected.has(name)) && !allBarcodeSelected,
    [selectedArray, barcodeSelected, allBarcodeSelected],
  );

  // MUI の Checkbox は indeterminate を data-indeterminate 属性にしか反映しないため、
  // ネイティブ input の indeterminate プロパティは ref 経由で手動設定する。
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const barcodeHeaderCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);
  useEffect(() => {
    if (barcodeHeaderCheckboxRef.current) {
      barcodeHeaderCheckboxRef.current.indeterminate = someBarcodeSelected;
    }
  }, [someBarcodeSelected]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        setBarcodeSelected((barcodePrev) => {
          const barcodeNext = new Set(barcodePrev);
          barcodeNext.delete(name);
          return barcodeNext;
        });
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const toggleBarcode = (name: string) => {
    setBarcodeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAllSelected = (checked: boolean) => {
    if (checked) {
      setSelected(new Set(shipperNames));
    } else {
      setSelected(new Set());
      setBarcodeSelected(new Set());
    }
  };

  const toggleAllBarcode = (checked: boolean) => {
    setBarcodeSelected(checked ? new Set(selected) : new Set());
  };

  const handleConfirm = () => {
    const selectedArr = Array.from(selected);
    const barcodeArr = barcodeSelectable ? Array.from(barcodeSelected) : [];
    if (sortModeSelectable) {
      onConfirm(selectedArr, barcodeArr, sortMode);
    } else if (barcodeSelectable) {
      onConfirm(selectedArr, barcodeArr);
    } else {
      onConfirm(selectedArr);
    }
  };

  return (
    <Dialog open={open} onClose={onBack} maxWidth="sm" fullWidth>
      <DialogTitle>荷主の選択</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          出力対象の荷主を選択してください（複数選択可）。
        </Typography>
        {barcodeSelectable && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            「バーコード」をONにした荷主は、伝票Noをバーコードで出力します。
          </Typography>
        )}
        {sortModeSelectable && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
              出力順
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sortMode}
              onChange={(_, value) => { if (value) setSortMode(value as PickingSortMode); }}
            >
              <ToggleButton value="course-shipper" sx={{ textTransform: 'none', px: 1.5 }}>
                コース ▶ 荷主
              </ToggleButton>
              <ToggleButton value="shipper-course" sx={{ textTransform: 'none', px: 1.5 }}>
                荷主 ▶ コース
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {isLoading && <Typography>読み込み中...</Typography>}
        {isError && <Typography color="error">荷主一覧の取得に失敗しました。</Typography>}
        {!isLoading && !isError && shipperNames.length === 0 && (
          <Typography color="text.secondary">期間内に荷主が登録されていません。</Typography>
        )}
        {!isLoading && !isError && shipperNames.length > 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    inputRef={headerCheckboxRef}
                    onChange={(e) => toggleAllSelected(e.target.checked)}
                  />
                )}
                label={<Typography variant="caption" sx={{ fontWeight: 600 }}>荷主</Typography>}
              />
              {barcodeSelectable && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={allBarcodeSelected}
                      indeterminate={someBarcodeSelected}
                      disabled={selected.size === 0}
                      inputRef={barcodeHeaderCheckboxRef}
                      onChange={(e) => toggleAllBarcode(e.target.checked)}
                    />
                  )}
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>バーコード</Typography>}
                  sx={{ mr: 0 }}
                />
              )}
            </Box>
            <List dense disablePadding>
              {shipperNames.map((name) => (
                <ListItem key={name} sx={{ px: 2, py: 0.25, display: 'flex', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={selected.has(name)}
                        onChange={() => toggle(name)}
                        size="small"
                      />
                    )}
                    label={name}
                  />
                  {barcodeSelectable && (
                    <Checkbox
                      checked={barcodeSelected.has(name)}
                      disabled={!selected.has(name)}
                      inputProps={{ 'aria-label': `${name} バーコード` }}
                      onChange={() => toggleBarcode(name)}
                      size="small"
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onBack}>戻る</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={selected.size === 0}>
          出力へ進む
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ShipperSelectDialog;
